import { DEFAULT_BREAKPOINT_NAME } from './constants';
import { sortBoxesByReadingOrder, sortBoxesBySlices } from './autoplacementSortUtils';
import { findFirstIndex, rowsOverlap } from './utils';
import { DEFAULT_PEGBOARD_ROW_SIZE_DEFINITION } from './layoutBuilderUtils';

// TODO: Refactor with GridBoundingBox type from utils.ts

/**
 * Tracks rectangles placed on the pegboard and the current row extent.
 * `maxRow` is the last occupied row (inclusive) or the container's initial row
 * count, whichever is greater. `generateSearchPositions` searches up to
 * `maxRow + rowsSpanned - 1`, allowing one element to overflow the boundary.
 */

/**
 * Finds the closest available position on the pegboard for a rect of the given
 * size, records it in the pegboard occupancy, and returns the placed position.
 *
 * The search scans top-to-bottom, left-to-right starting from (`row`, `column`).
 * If no position fits within the current grid bounds, falls back to the first
 * column of the row immediately after `maxRow` and reports `rowsNeededToFit` so
 * the caller can expand the container layout accordingly.
 */
export function findAndPlaceOnPegboard({
  pegboardOccupancy,
  pegboardContainerNode,
  row,
  column,
  rowsSpanned,
  columnsSpanned,
  targetBreakpoint = DEFAULT_BREAKPOINT_NAME
}) {
  const containerLayout = pegboardContainerNode.getContainerLayout(targetBreakpoint);
  const maxCol = containerLayout.columns.length;
  const maxRow = pegboardOccupancy.maxRow;
  for (const {
    row: destRow,
    column: destCol
  } of generateSearchPositions({
    row,
    column,
    rowsSpanned,
    columnsSpanned,
    maxRow,
    maxCol
  })) {
    const rowsNeededToFit = Math.max(0, destRow + rowsSpanned - 1 - maxRow);
    if (!isIntersecting(pegboardOccupancy.rects, destRow, destCol, rowsSpanned, columnsSpanned)) {
      addPlacedRect(pegboardOccupancy, destRow, destCol, rowsSpanned, columnsSpanned);
      return {
        row: destRow,
        column: destCol,
        rowsNeededToFit
      };
    }
  }
  return {
    row: maxRow + 1,
    column: 1,
    rowsNeededToFit: rowsSpanned
  };
}

/**
 * Yields candidate positions (row, column) for placing a rect of the given size,
 * scanning top-to-bottom, left-to-right starting from (`row`, `column`).
 * Rows are searched up to `maxRow + rowsSpanned - 1` so that a rect whose top
 * edge starts within the current grid can still extend one or more rows past it.
 */
function* generateSearchPositions({
  row,
  column,
  rowsSpanned,
  columnsSpanned,
  maxRow,
  maxCol
}) {
  for (let rowCandidate = row; rowCandidate <= maxRow + rowsSpanned - 1; rowCandidate++) {
    for (let colCandidate = column; colCandidate + columnsSpanned - 1 <= maxCol; colCandidate++) {
      yield {
        row: rowCandidate,
        column: colCandidate
      };
    }
  }
}

/**
 * Returns `true` if the candidate rect overlaps any rect in `rects` using an
 * axis-aligned bounding box test.
 */
function isIntersecting(rects, rowStart, colStart, rowsSpanned, columnsSpanned) {
  for (const rect of rects) {
    if (rowStart < rect.rowStart + rect.rowsSpanned && rect.rowStart < rowStart + rowsSpanned && colStart < rect.colStart + rect.columnsSpanned && rect.colStart < colStart + columnsSpanned) {
      return true;
    }
  }
  return false;
}

/**
 * Orders pegboard children for autoplacement so that manually-user-positioned
 * nodes come first (in reading order), followed by auto-placed nodes ordered by
 * a slicing decomposition of the reference-breakpoint layout. Nodes without a
 * reference-breakpoint position are appended last.
 *
 * Manually positioned nodes are placed before auto-placed ones so that their
 * occupied cells are registered in the pegboard occupancy before autoplacement runs.
 */
export function getChildrenByStackingOrder(tree, parentNode, targetBreakpoint, referenceBreakpoint) {
  const nodesById = {};
  const manualBoxes = [];
  const autoBoxes = [];
  const withoutBox = [];
  for (const child of parentNode.getVisibleChildNodes(targetBreakpoint)) {
    const bbox = tree.getBoundingBoxOfPegboardNode(child, referenceBreakpoint);

    // Defensive fallback, but in practice should never happen in a valid pegboard.
    if (bbox === null) {
      withoutBox.push(child);
      continue;
    }
    nodesById[child.getId()] = child;
    if (child.isManuallyPositionedByUserOnPegboard(targetBreakpoint)) {
      manualBoxes.push(bbox);
    } else {
      autoBoxes.push(bbox);
    }
  }
  const getModuleFromBoundingBox = bbox => nodesById[bbox.id];
  return [...sortBoxesByReadingOrder(manualBoxes).map(getModuleFromBoundingBox), ...sortBoxesBySlices(autoBoxes).map(getModuleFromBoundingBox), ...withoutBox];
}

/** Creates a `PegboardOccupancy` sized to the container's initial row count. */
export function buildEmptyPegboardOccupancy(rows) {
  return {
    rects: [],
    maxRow: rows
  };
}

/**
 * Records a placed rectangle in the pegboard occupancy and extends `maxRow` if the
 * rect reaches past the current bound. `maxRow` tracks the last occupied row (inclusive).
 */
export function addPlacedRect(pegboardOccupancy, rowStart, colStart, rowsSpanned, columnsSpanned) {
  pegboardOccupancy.rects.push({
    rowStart,
    colStart,
    rowsSpanned,
    columnsSpanned
  });
  if (rowStart + rowsSpanned - 1 > pegboardOccupancy.maxRow) {
    pegboardOccupancy.maxRow = rowStart + rowsSpanned - 1;
  }
}

/**
 * Builds a list of pegboard bounding boxes for the given module IDs at the
 * specified breakpoint, omitting any modules without a visible position.
 */
export function buildPegboardBoundingBoxes({
  tree,
  modules,
  breakpointName
}) {
  return modules.flatMap(module => {
    const bbox = tree.getBoundingBoxOfPegboardNode(module, breakpointName);
    return bbox !== null ? [bbox] : [];
  }).sort((a, b) => a.rowTop - b.rowTop);
}

/**
 * Sums the heights of all empty bands (sorted ascending by `rowTop`) that fall
 * entirely above `rowStart`.
 */
function computeUpwardShift(freedRowBands, rowStart) {
  let shift = 0;
  for (const band of freedRowBands) {
    if (band.rowBottom < rowStart) {
      shift += band.rowBottom - band.rowTop + 1;
    } else {
      break;
    }
  }
  return shift;
}

/**
 * Returns true if any element in `sortedBoundingBoxes` (sorted ascending by
 * rowTop) shares one or more rows with `targetBoundingBox`. Uses binary search
 * to limit the scan.
 */
function hasSiblingWithRowOverlap(sortedBoundingBoxes, targetBoundingBox) {
  const limit = findFirstIndex(sortedBoundingBoxes, e => e.rowTop > targetBoundingBox.rowBottom);
  for (let i = limit - 1; i >= 0; i--) {
    if (rowsOverlap(sortedBoundingBoxes[i], targetBoundingBox)) return true;
  }
  return false;
}

/**
 * Computes the freed row bands from a set of deleted module bounding boxes
 * and the remaining modules after deletion. A deleted element's row range
 * becomes a freed band only when no remaining element in any column shares
 * its rows; side-by-side elements block the row entirely. Adjacent or
 * overlapping bands are merged so upward shift is never double-counted.
 */
export function computeFreedRowBands(sortedDeletedBoundingBoxes, sortedRemainingBoundingBoxes) {
  const freedRowBands = [];
  for (const deleted of sortedDeletedBoundingBoxes) {
    if (!hasSiblingWithRowOverlap(sortedRemainingBoundingBoxes, deleted)) {
      const prev = freedRowBands[freedRowBands.length - 1];
      if (prev && deleted.rowTop <= prev.rowBottom + 1) {
        prev.rowBottom = Math.max(prev.rowBottom, deleted.rowBottom);
      } else {
        freedRowBands.push({
          rowTop: deleted.rowTop,
          rowBottom: deleted.rowBottom
        });
      }
    }
  }
  return freedRowBands;
}

/**
 * Applies upward shift to a single element based on freed row bands, and
 * returns the updated tree along with the element's final position and
 * spanning. Callers are responsible for iterating over elements and deciding
 * trim. Upward-only shift cannot exceed the container bounds.
 */
export function shiftElementByFreedRowBands(tree, child, freedRowBands, targetBreakpoint) {
  const {
    rowStart,
    columnStart
  } = tree.getPositionOfPegboardNode(child, targetBreakpoint);
  const {
    rowsSpanned = 1,
    columnsSpanned = 1
  } = tree.getSpanningOfPegboardNode(child, targetBreakpoint);
  const newRowStart = rowStart - computeUpwardShift(freedRowBands, rowStart);
  if (newRowStart === rowStart) {
    return {
      tree,
      rowStart,
      columnStart,
      rowsSpanned,
      columnsSpanned
    };
  }
  const {
    tree: result
  } = tree.placeNodeOnPegboardBySystemAction({
    parentNodeId: child.getParentNodeId(),
    targetNodeId: child.getId(),
    row: newRowStart,
    column: columnStart,
    rowsSpanned,
    columnsSpanned,
    breakpoint: targetBreakpoint
  });
  return {
    tree: result,
    rowStart: newRowStart,
    columnStart,
    rowsSpanned,
    columnsSpanned
  };
}

/**
 * Removes trailing empty rows from a pegboard container.
 *
 * When `previousMinRowCount` is provided the trim is bounded to the rows freed
 * by the most recent operation, leaving any rows that were already trailing-
 * empty before the operation untouched.
 */
export function trimTrailingPegboardRows(tree, parentNodeId, breakpointName, options) {
  const parentNode = tree.findNode(parentNodeId);
  if (!parentNode.isPegboardContainer()) {
    return tree;
  }
  const containerLayout = parentNode.getContainerLayout(breakpointName);
  const {
    minRowCount
  } = parentNode.getMinRowCount(breakpointName);
  const rowsAvailableToTrim = containerLayout.rows.length - minRowCount;
  if (rowsAvailableToTrim <= 0) {
    return tree;
  }
  const {
    previousMinRowCount
  } = options !== null && options !== void 0 ? options : {};
  const rowsToTrim = previousMinRowCount !== undefined ? Math.min(rowsAvailableToTrim, previousMinRowCount - minRowCount) : rowsAvailableToTrim;
  if (rowsToTrim <= 0) {
    return tree;
  }
  const {
    tree: result
  } = tree.mergeContainerLayout(parentNodeId, breakpointName, Object.assign({}, containerLayout, {
    rows: containerLayout.rows.slice(0, containerLayout.rows.length - rowsToTrim)
  }));
  return result;
}

/**
 * Expands the pegboard container to accommodate `requiredLastRow` if it exceeds
 * the container's current row count. Returns the tree unchanged if no expansion
 * is needed.
 */
export function expandPegboardIfNeeded(tree, parentNodeId, breakpointName, containerLayout, requiredLastRow) {
  if (requiredLastRow <= containerLayout.rows.length) {
    return tree;
  }
  const {
    tree: result
  } = tree.mergeContainerLayout(parentNodeId, breakpointName, Object.assign({}, containerLayout, {
    rows: Array.from({
      length: requiredLastRow
    }, () => ({
      size: DEFAULT_PEGBOARD_ROW_SIZE_DEFINITION
    }))
  }));
  return result;
}

/**
 * Resolves the row and column span to use when autoplacing `node` at
 * `targetBreakpoint`.
 *
 * Precedence for column span:
 * 1. `targetColumnsSpanned` if explicitly provided.
 * 2. The node's existing span if it fits within the container width.
 * 3. Full container width as a fallback.
 *
 * Throws if the node's parent is not a pegboard container.
 */
export function computeTargetBreakpointSpanningForAutoplacement({
  tree,
  node,
  targetRowsSpanned,
  targetColumnsSpanned,
  targetBreakpoint = DEFAULT_BREAKPOINT_NAME,
  isNewNode = false
}) {
  var _ref;
  const parentNode = node.getParentNode();
  if (!parentNode.isPegboardContainer()) {
    throw new Error('Parent node is not a pegboard container');
  }
  const containerLayout = parentNode.getContainerLayout(targetBreakpoint);
  const nodeSpanning = tree.getSpanningOfPegboardNode(node, targetBreakpoint, !isNewNode);
  const rowsSpannedToUse = (_ref = targetRowsSpanned !== null && targetRowsSpanned !== void 0 ? targetRowsSpanned : nodeSpanning.rowsSpanned) !== null && _ref !== void 0 ? _ref : 1;
  let columnsSpannedToUse;
  if (targetColumnsSpanned) {
    columnsSpannedToUse = targetColumnsSpanned;
  } else if (nodeSpanning.columnsSpanned && 1 + nodeSpanning.columnsSpanned <= containerLayout.columns.length) {
    columnsSpannedToUse = nodeSpanning.columnsSpanned;
  } else {
    // Fallback to the full width of the container.
    columnsSpannedToUse = containerLayout.columns.length || 1;
  }
  return {
    rowsSpanned: rowsSpannedToUse,
    columnsSpanned: columnsSpannedToUse
  };
}